import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Button,
  IconButton,
  useToast,
  Badge,
  Alert,
  AlertIcon,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  CardHeader,
  Skeleton,
  SkeletonText,
  Icon,
  Flex,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react'
import {
  FiArrowLeft,
  FiEdit2,
  FiUsers,
  FiSettings,
  FiMail,
  FiUser,
  FiFile,
  FiFolderPlus,
  FiTag,
  FiMessageSquare,
  FiTrendingUp,
  FiSlash,
  FiCheckCircle,
  FiTrash2,
  FiUserX,
  FiUserCheck,
  FiList,
  FiLock,
  FiUserPlus,
  FiLogIn,
} from 'react-icons/fi'
import { useAdminGuard } from '@/hooks/useAdminGuard'
import { useAuth } from '@/contexts/AuthContext'
import { Organization, User } from '@/types'
import apiService from '@/services/api'
import { StatusBadge, RoleBadge, PageHeader, UsageStatsCard } from '@/components/Common'
import InviteUserModal from '@/components/Admin/InviteUserModal'
import AddUserModal from '@/components/Admin/AddUserModal'
import DeleteOrganizationDialog from '@/components/Admin/DeleteOrganizationDialog'
import UserAuditLogModal from '@/components/Admin/UserAuditLogModal'
import { useAppTranslation } from '@/contexts/LanguageContext'

export default function OrganizationDetailsPage() {
  const { id } = useParams<{ id: string }>()
  const { isAuthorized, ForbiddenView } = useAdminGuard()
  const { impersonate } = useAuth()
  const [impersonatingUserId, setImpersonatingUserId] = useState<number | null>(null)
  const navigate = useNavigate()
  const toast = useToast()
  const { t } = useAppTranslation('admin')

  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState(true)
  const [togglingOrg, setTogglingOrg] = useState(false)
  const [togglingUserId, setTogglingUserId] = useState<number | null>(null)
  const [targetUser, setTargetUser] = useState<User | null>(null)
  const [auditLogUser, setAuditLogUser] = useState<User | null>(null)

  const { isOpen: isInviteOpen, onOpen: onInviteOpen, onClose: onInviteClose } = useDisclosure()
  const { isOpen: isAddUserOpen, onOpen: onAddUserOpen, onClose: onAddUserClose } = useDisclosure()
  const { isOpen: isAuditLogOpen, onOpen: onAuditLogOpen, onClose: onAuditLogClose } = useDisclosure()
  const {
    isOpen: isOrgToggleOpen,
    onOpen: onOrgToggleOpen,
    onClose: onOrgToggleClose,
  } = useDisclosure()
  const {
    isOpen: isUserToggleOpen,
    onOpen: onUserToggleOpen,
    onClose: onUserToggleClose,
  } = useDisclosure()
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure()

  const cancelRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (id) {
      loadOrganization()
    }
  }, [id])

  const loadOrganization = async () => {
    try {
      setLoading(true)
      const organization = await apiService.getAdminOrganization(parseInt(id!))
      setOrganization(organization)
    } catch (error: any) {
      toast({
        title: t('organizationDetails.toast.loadError'),
        description:
          error.response?.data?.message || t('organizationDetails.toast.loadErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = () => {
    navigate(`/admin/organizations/${id}/edit`)
  }

  const handleBack = () => {
    navigate('/admin/organizations')
  }

  const handleInviteSuccess = () => {
    loadOrganization()
    onInviteClose()
  }

  const handleAddUserSuccess = () => {
    loadOrganization()
    onAddUserClose()
  }

  const handleImpersonate = async (user: User) => {
    setImpersonatingUserId(user.id)
    try {
      await impersonate(user.id)
      window.location.href = '/'
      return
    } catch (error: any) {
      toast({
        title: 'Could not log in as user',
        description:
          error.response?.data?.errors?.user?.[0] ||
          error.response?.data?.error ||
          'Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setImpersonatingUserId(null)
    }
  }

  const handleResetPassword = async (user: User) => {
    if (!organization) return
    try {
      const data = await apiService.resetUserPassword(organization.id, user.id)
      toast({
        title: t('resetPassword.toast.success'),
        description: t('resetPassword.toast.copied', { password: data.temporary_password }),
        status: 'success',
        duration: null,
        isClosable: true,
      })
    } catch {
      toast({
        title: t('resetPassword.toast.error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleToggleOrgStatus = async () => {
    if (!organization) return
    setTogglingOrg(true)
    try {
      await apiService.toggleOrganizationStatus(organization.id)
      toast({
        title: organization.active
          ? t('organizationDetails.toast.orgDisabled')
          : t('organizationDetails.toast.orgEnabled'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      await loadOrganization()
    } catch (error: any) {
      toast({
        title: t('organizationDetails.toast.toggleOrgError'),
        description: error.response?.data?.error || t('organizationDetails.toast.toggleOrgErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setTogglingOrg(false)
      onOrgToggleClose()
    }
  }

  const handleToggleUserStatus = async () => {
    if (!organization || !targetUser) return
    setTogglingUserId(targetUser.id)
    try {
      await apiService.toggleUserStatus(organization.id, targetUser.id)
      toast({
        title: targetUser.is_active
          ? t('organizationDetails.toast.userDisabled', { name: targetUser.name })
          : t('organizationDetails.toast.userEnabled', { name: targetUser.name }),
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      await loadOrganization()
    } catch (error: any) {
      toast({
        title: t('organizationDetails.toast.toggleUserError'),
        description: error.response?.data?.error || t('organizationDetails.toast.toggleUserErrorDescription'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setTogglingUserId(null)
      setTargetUser(null)
      onUserToggleClose()
    }
  }

  const openUserToggleDialog = (user: User) => {
    setTargetUser(user)
    onUserToggleOpen()
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getAdminUser = (): User | undefined => {
    return organization?.users?.find((u) => u.role === 'admin' || u.role === 'super')
  }

  if (!isAuthorized) return ForbiddenView

  if (loading) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <VStack spacing={6} align="stretch">
          <HStack>
            <Skeleton height="32px" width="150px" />
            <Skeleton height="32px" width="100px" />
          </HStack>
          <SkeletonText noOfLines={2} spacing={4} />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} height="100px" />
            ))}
          </SimpleGrid>
          <Skeleton height="200px" />
        </VStack>
      </Box>
    )
  }

  if (!organization) {
    return (
      <Box p={{ base: 4, md: 6 }}>
        <Alert status="error">
          <AlertIcon />
          {t('organizationDetails.notFound')}
        </Alert>
      </Box>
    )
  }

  const adminUser = getAdminUser()
  const users = organization.users || []
  const pendingInvitations = organization.pending_invitations || []
  const usageStats = organization.usage_stats

  // Everything here already ships on the single getAdminOrganization response —
  // no extra requests. Each entry is dropped when its source field is absent.
  const planLimits = organization.plan?.limits
  const orgLanguage = organization.language ?? organization.settings?.language
  const fileLimit = planLimits?.file_limit ?? organization.settings?.file_limit
  const formatLimit = (value?: number) =>
    value === undefined || value === null
      ? undefined
      : value < 0
        ? t('organizationDetails.additional.unlimited')
        : value.toLocaleString()

  const additionalInfoFields: Array<{
    label: string
    value?: string
    node?: React.ReactNode
    mono?: boolean
  }> = [
    {
      label: t('organizationDetails.additional.organizationId'),
      value: String(organization.id),
      mono: true,
    },
    {
      label: t('organizationDetails.additional.slug'),
      value: organization.slug,
      mono: true,
    },
    {
      label: t('organizationDetails.additional.status'),
      node: <StatusBadge status={organization.active ? 'active' : 'inactive'} />,
    },
    organization.plan && {
      label: t('organizationDetails.additional.plan'),
      node: (
        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight="medium">
            {organization.plan.name}
          </Text>
          <Badge colorScheme={organization.plan.type === 'custom' ? 'purple' : 'blue'} fontSize="xs">
            {t(`organizationDetails.additional.planType.${organization.plan.type}`)}
          </Badge>
        </HStack>
      ),
    },
    organization.plan?.price_monthly !== undefined && {
      label: t('organizationDetails.additional.monthlyPrice'),
      value: `$${Number(organization.plan.price_monthly).toLocaleString()}`,
    },
    {
      label: t('organizationDetails.additional.usersLimit'),
      value: formatLimit(planLimits?.users_limit),
    },
    {
      label: t('organizationDetails.additional.filesLimit'),
      value:
        fileLimit === undefined
          ? undefined
          : organization.settings?.current_file_count !== undefined
            ? `${organization.settings.current_file_count.toLocaleString()} / ${formatLimit(fileLimit)}`
            : formatLimit(fileLimit),
    },
    {
      label: t('organizationDetails.additional.pagesLimit'),
      value: formatLimit(planLimits?.page_limit),
    },
    {
      label: t('organizationDetails.additional.storageLimit'),
      value:
        planLimits?.storage_limit_gb === undefined
          ? undefined
          : planLimits.storage_limit_gb < 0
            ? t('organizationDetails.additional.unlimited')
            : `${planLimits.storage_limit_gb.toLocaleString()} GB`,
    },
    {
      label: t('organizationDetails.additional.chatbotQueriesLimit'),
      value: formatLimit(planLimits?.chatbot_queries_limit),
    },
    orgLanguage && {
      label: t('organizationDetails.additional.language'),
      value: orgLanguage === 'es' ? 'Español' : 'English',
    },
    organization.access_expires_at && {
      label: t('organizationDetails.additional.accessExpiresAt'),
      value: organization.access_expires_at,
    },
    {
      label: t('organizationDetails.additional.createdAt'),
      value: formatDate(organization.created_at),
    },
    {
      label: t('organizationDetails.additional.lastUpdated'),
      value: formatDate(organization.updated_at),
    },
  ]
    .filter((field): field is Exclude<typeof field, false | undefined | null | ''> => Boolean(field))
    .filter((field) => field.node !== undefined || field.value !== undefined)

  return (
    <Box p={{ base: 4, md: 6 }}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Button
            variant="ghost"
            leftIcon={<FiArrowLeft />}
            onClick={handleBack}
            mb={4}
            size="sm"
          >
            {t('organizationDetails.backToOrganizations')}
          </Button>

          <PageHeader
            title={organization.name}
            description={organization.description || t('organizationDetails.noDescription')}
            actions={[
              {
                label: organization.active
                  ? t('organizationDetails.disableOrganization')
                  : t('organizationDetails.enableOrganization'),
                icon: organization.active ? <FiSlash /> : <FiCheckCircle />,
                onClick: onOrgToggleOpen,
                colorScheme: organization.active ? 'red' : 'green',
                variant: 'outline',
                isLoading: togglingOrg,
              },
              {
                label: t('organizationDetails.editOrganization'),
                icon: <FiEdit2 />,
                onClick: handleEdit,
              },
              {
                label: t('deleteOrganization.action'),
                icon: <FiTrash2 />,
                onClick: onDeleteOpen,
                colorScheme: 'red',
              },
            ]}
          >
            <HStack spacing={3} mt={2}>
              <StatusBadge status={organization.active ? 'active' : 'inactive'} />
              {(organization.language ?? organization.settings?.language) && (
                <Badge colorScheme="teal">
                  {(organization.language ?? organization.settings?.language) === 'es'
                    ? 'Español'
                    : 'English'}
                </Badge>
              )}
              {organization.access_expires_at &&
                (new Date(`${organization.access_expires_at}T23:59:59`) < new Date() ? (
                  <Badge colorScheme="red">
                    {t('organizationDetails.accessExpired', {
                      date: organization.access_expires_at,
                    })}
                  </Badge>
                ) : (
                  <Badge colorScheme="orange">
                    {t('organizationDetails.accessExpires', {
                      date: organization.access_expires_at,
                    })}
                  </Badge>
                ))}
              <Text color="gray.500" fontSize="sm">
                {t('organizationDetails.slug')}{' '}
                <Text as="span" fontFamily="mono">
                  {organization.slug}
                </Text>
              </Text>
            </HStack>
          </PageHeader>
        </Box>

        {/* Overview Stats */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
          <Stat bg="blue.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="blue.200">
            <StatLabel color="blue.600">
              <HStack>
                <Icon as={FiUsers} />
                <Text>{t('organizationDetails.stats.totalUsers')}</Text>
              </HStack>
            </StatLabel>
            <StatNumber color="blue.800" fontSize="2xl">
              {users.length}
            </StatNumber>
            <StatHelpText>{t('organizationDetails.stats.organizationMembers')}</StatHelpText>
          </Stat>

          <Stat bg="green.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="green.200">
            <StatLabel color="green.600">
              <HStack>
                <Icon as={FiFile} />
                <Text>{t('organizationDetails.stats.files')}</Text>
              </HStack>
            </StatLabel>
            <StatNumber color="green.800" fontSize="2xl">
              {organization.files_count || 0}
            </StatNumber>
            <StatHelpText>{t('organizationDetails.stats.totalFilesUploaded')}</StatHelpText>
          </Stat>

          <Stat bg="purple.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="purple.200">
            <StatLabel color="purple.600">
              <HStack>
                <Icon as={FiFolderPlus} />
                <Text>{t('organizationDetails.stats.folders')}</Text>
              </HStack>
            </StatLabel>
            <StatNumber color="purple.800" fontSize="2xl">
              {organization.folders_count || 0}
            </StatNumber>
            <StatHelpText>{t('organizationDetails.stats.organizationFolders')}</StatHelpText>
          </Stat>

          <Stat bg="orange.50" p={4} borderRadius="lg" borderWidth="1px" borderColor="orange.200">
            <StatLabel color="orange.600">
              <HStack>
                <Icon as={FiFile} />
                <Text>{t('organizationDetails.stats.totalPages')}</Text>
              </HStack>
            </StatLabel>
            <StatNumber color="orange.800" fontSize="2xl">
              {organization.total_pages || 0}
            </StatNumber>
            <StatHelpText>{t('organizationDetails.stats.pagesAcrossDocuments')}</StatHelpText>
          </Stat>
        </SimpleGrid>

        {/* Usage Statistics */}
        {usageStats && (
          <Box>
            <Heading size="sm" mb={4} color="gray.700">
              <HStack spacing={2}>
                <Icon as={FiTrendingUp} color="purple.500" boxSize={4} />
                <Text>{t('organizationDetails.usage.title')}</Text>
              </HStack>
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <UsageStatsCard
                title={t('organizationDetails.usage.smartTagging')}
                icon={FiTag}
                stats={usageStats.smart_tagging}
                colorScheme="blue"
                showTokens={true}
              />
              <UsageStatsCard
                title={t('organizationDetails.usage.smartRenaming')}
                icon={FiEdit2}
                stats={usageStats.smart_renaming}
                colorScheme="green"
                showTokens={true}
              />
              <UsageStatsCard
                title={t('organizationDetails.usage.chatbotQueries')}
                icon={FiMessageSquare}
                stats={usageStats.chatbot_queries}
                colorScheme="purple"
                showTokens={true}
              />
            </SimpleGrid>
          </Box>
        )}

        {/* Organization Details */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="stretch">
          {/* Admin User Info */}
          <Card variant="outline" shadow="none">
            <CardHeader pb={3} borderBottom="1px solid" borderColor="gray.100">
              <HStack spacing={2}>
                <Icon as={FiUser} color="gray.500" boxSize={4} />
                <Heading size="sm">{t('organizationDetails.owner.title')}</Heading>
              </HStack>
            </CardHeader>
            <CardBody pt={4}>
              {adminUser ? (
                <VStack align="start" spacing={3}>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      {t('organizationDetails.owner.fullName')}
                    </Text>
                    <Text fontWeight="medium">{adminUser.name}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      {t('organizationDetails.owner.emailAddress')}
                    </Text>
                    <Text fontWeight="medium">{adminUser.email}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      {t('organizationDetails.owner.role')}
                    </Text>
                    <RoleBadge role={adminUser.role} />
                  </Box>
                  <Box>
                    <Text fontSize="sm" color="gray.600" mb={1}>
                      {t('organizationDetails.owner.joined')}
                    </Text>
                    <Text fontSize="sm">{formatDate(adminUser.created_at)}</Text>
                  </Box>
                </VStack>
              ) : (
                <Text color="gray.500">{t('organizationDetails.owner.noAdmin')}</Text>
              )}
            </CardBody>
          </Card>

          {/* Pending Invitations */}
          <Card variant="outline" shadow="none">
            <CardHeader pb={3} borderBottom="1px solid" borderColor="gray.100">
              <Flex justify="space-between" align="center">
                <HStack spacing={2}>
                  <Icon as={FiMail} color="gray.500" boxSize={4} />
                  <Heading size="sm">{t('organizationDetails.invitations.title')}</Heading>
                  <Badge colorScheme="orange" size="sm">
                    {pendingInvitations.length}
                  </Badge>
                </HStack>
              </Flex>
            </CardHeader>
            <CardBody pt={4}>
              {pendingInvitations.length > 0 ? (
                <VStack align="stretch" spacing={3}>
                  {pendingInvitations.map((invitation) => (
                    <Box
                      key={invitation.id}
                      p={3}
                      bg="gray.50"
                      borderRadius="md"
                      borderWidth="1px"
                      borderColor="gray.200"
                    >
                      <Flex justify="space-between" align="center">
                        <Box>
                          <Text fontWeight="medium">{invitation.email}</Text>
                          <Text fontSize="sm" color="gray.600">
                            {t('organizationDetails.invitations.invitedBy', {
                              name: invitation.invited_by,
                              date: formatDate(invitation.expires_at).split(',')[0],
                            })}
                          </Text>
                        </Box>
                        <RoleBadge role={invitation.role as any} />
                      </Flex>
                    </Box>
                  ))}
                </VStack>
              ) : (
                <Text color="gray.500">{t('organizationDetails.invitations.noPending')}</Text>
              )}
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Users Management */}
        <Card variant="outline" shadow="none">
          <CardHeader pb={3} borderBottom="1px solid" borderColor="gray.100">
            <Flex justify="space-between" align="center">
              <HStack spacing={2}>
                <Icon as={FiUsers} color="gray.500" boxSize={4} />
                <Heading size="sm">{t('organizationDetails.members.title')}</Heading>
                <Badge colorScheme="blue" size="sm">
                  {users.length}
                </Badge>
              </HStack>
              <HStack spacing={2}>
                <Button leftIcon={<FiUserPlus />} colorScheme="blue" size="sm" onClick={onAddUserOpen}>
                  {t('addUser.buttonLabel')}
                </Button>
                <Button leftIcon={<FiMail />} colorScheme="green" size="sm" onClick={onInviteOpen}>
                  {t('organizationDetails.members.inviteUser')}
                </Button>
              </HStack>
            </Flex>
          </CardHeader>
          <CardBody pt={4}>
            {users.length > 0 ? (
              <VStack align="stretch" spacing={3}>
                {users.map((u) => (
                  <Box
                    key={u.id}
                    p={3}
                    bg={u.is_active === false ? 'red.50' : 'gray.50'}
                    borderRadius="md"
                    opacity={u.is_active === false ? 0.75 : 1}
                    borderWidth={u.is_active === false ? '1px' : '0px'}
                    borderColor={u.is_active === false ? 'red.200' : 'transparent'}
                  >
                    <Flex justify="space-between" align="center">
                      <Box>
                        <HStack spacing={2}>
                          <Text fontWeight="medium">{u.name}</Text>
                          {u.is_active === false && (
                            <Badge colorScheme="red" fontSize="xs">
                              {t('organizationDetails.members.disabled')}
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="sm" color="gray.600">
                          {u.email}
                        </Text>
                      </Box>
                      <HStack spacing={2}>
                        <RoleBadge role={u.role} />
                        <Text fontSize="xs" color="gray.500">
                          {formatDate(u.created_at).split(',')[0]}
                        </Text>
                        <Tooltip label={t('profile.audit.viewAuditLog', { ns: 'common' })}>
                          <IconButton
                            aria-label={t('profile.audit.viewAuditLog', { ns: 'common' })}
                            icon={<FiList />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => {
                              setAuditLogUser(u as User)
                              onAuditLogOpen()
                            }}
                          />
                        </Tooltip>
                        {u.role !== 'super' && (
                          <Tooltip
                            label={
                              u.is_active === false
                                ? t('organizationDetails.members.impersonateDisabledUserTooltip')
                                : organization?.active === false
                                  ? t('organizationDetails.members.impersonateDisabledOrgTooltip')
                                  : t('organizationDetails.members.impersonateLoginAs')
                            }
                          >
                            <IconButton
                              aria-label={
                                u.is_active === false
                                  ? t('organizationDetails.members.impersonateDisabledUserTooltip')
                                  : organization?.active === false
                                    ? t('organizationDetails.members.impersonateDisabledOrgTooltip')
                                    : t('organizationDetails.members.impersonateLoginAs')
                              }
                              icon={<FiLogIn />}
                              size="sm"
                              variant="ghost"
                              colorScheme="purple"
                              isLoading={impersonatingUserId === u.id}
                              isDisabled={
                                u.is_active === false || organization?.active === false
                              }
                              onClick={() => handleImpersonate(u as User)}
                            />
                          </Tooltip>
                        )}
                        <Tooltip label={t('resetPassword.buttonLabel')}>
                          <IconButton
                            aria-label={t('resetPassword.buttonLabel')}
                            icon={<FiLock />}
                            size="sm"
                            variant="ghost"
                            colorScheme="orange"
                            onClick={() => handleResetPassword(u as User)}
                          />
                        </Tooltip>
                        <Tooltip
                          label={
                            u.is_active === false
                              ? t('organizationDetails.members.enableUser')
                              : t('organizationDetails.members.disableUser')
                          }
                        >
                          <IconButton
                            aria-label={
                              u.is_active === false
                                ? t('organizationDetails.members.enableUser')
                                : t('organizationDetails.members.disableUser')
                            }
                            icon={u.is_active === false ? <FiUserCheck /> : <FiUserX />}
                            size="sm"
                            variant="ghost"
                            colorScheme={u.is_active === false ? 'green' : 'red'}
                            isLoading={togglingUserId === u.id}
                            onClick={() => openUserToggleDialog(u as User)}
                          />
                        </Tooltip>
                      </HStack>
                    </Flex>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text color="gray.500">{t('organizationDetails.members.noUsers')}</Text>
            )}
          </CardBody>
        </Card>

        {/* Additional Info */}
        <Card variant="outline" shadow="none">
          <CardHeader pb={3} borderBottom="1px solid" borderColor="gray.100">
            <HStack spacing={2}>
              <Icon as={FiSettings} color="gray.500" boxSize={4} />
              <Heading size="sm">{t('organizationDetails.additional.title')}</Heading>
            </HStack>
          </CardHeader>
          <CardBody pt={4}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacingX={6} spacingY={5}>
              {additionalInfoFields.map((field) => (
                <Box key={field.label}>
                  <Text fontSize="sm" color="gray.600" mb={1}>
                    {field.label}
                  </Text>
                  {field.node ?? (
                    <Text fontSize="sm" fontWeight="medium" fontFamily={field.mono ? 'mono' : undefined}>
                      {field.value}
                    </Text>
                  )}
                </Box>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      </VStack>

      {/* Audit Log Modal */}
      {auditLogUser && (
        <UserAuditLogModal
          isOpen={isAuditLogOpen}
          onClose={() => {
            onAuditLogClose()
            setAuditLogUser(null)
          }}
          userId={auditLogUser.id}
          userName={auditLogUser.name}
        />
      )}

      {/* Invite User Modal */}
      {organization && (
        <InviteUserModal
          isOpen={isInviteOpen}
          onClose={onInviteClose}
          organizationId={organization.id}
          onSuccess={handleInviteSuccess}
        />
      )}

      {/* Add User Modal */}
      {organization && (
        <AddUserModal
          isOpen={isAddUserOpen}
          onClose={onAddUserClose}
          organizationId={organization.id}
          onSuccess={handleAddUserSuccess}
        />
      )}

      {/* Delete Organization Confirmation */}
      {organization && (
        <DeleteOrganizationDialog
          isOpen={isDeleteOpen}
          onClose={onDeleteClose}
          organization={organization}
          onDeleted={() => navigate('/admin/organizations')}
        />
      )}

      {/* Organization Toggle Confirmation */}
      <AlertDialog
        isOpen={isOrgToggleOpen}
        leastDestructiveRef={cancelRef}
        onClose={onOrgToggleClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {organization?.active
                ? t('organizationDetails.toggleOrg.disableTitle')
                : t('organizationDetails.toggleOrg.enableTitle')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {organization?.active
                ? t('organizationDetails.toggleOrg.disableMessage', { name: organization?.name })
                : t('organizationDetails.toggleOrg.enableMessage', { name: organization?.name })}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onOrgToggleClose}>
                {t('organizationDetails.toggleOrg.cancel')}
              </Button>
              <Button
                colorScheme={organization?.active ? 'red' : 'green'}
                onClick={handleToggleOrgStatus}
                ml={3}
                isLoading={togglingOrg}
              >
                {organization?.active
                  ? t('organizationDetails.toggleOrg.confirmDisable')
                  : t('organizationDetails.toggleOrg.confirmEnable')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* User Toggle Confirmation */}
      <AlertDialog
        isOpen={isUserToggleOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          setTargetUser(null)
          onUserToggleClose()
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {targetUser?.is_active
                ? t('organizationDetails.toggleUser.disableTitle')
                : t('organizationDetails.toggleUser.enableTitle')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {targetUser?.is_active
                ? t('organizationDetails.toggleUser.disableMessage', { name: targetUser?.name })
                : t('organizationDetails.toggleUser.enableMessage', { name: targetUser?.name })}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => {
                  setTargetUser(null)
                  onUserToggleClose()
                }}
              >
                {t('organizationDetails.toggleUser.cancel')}
              </Button>
              <Button
                colorScheme={targetUser?.is_active ? 'red' : 'green'}
                onClick={handleToggleUserStatus}
                ml={3}
                isLoading={togglingUserId !== null}
              >
                {targetUser?.is_active
                  ? t('organizationDetails.toggleUser.confirmDisable')
                  : t('organizationDetails.toggleUser.confirmEnable')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}
